/**
 * Admin Web Clipper — paste a URL of an article / case study, server scrapes
 * the page for title + description + image candidates, you pick the keepers
 * and one click sends it into the Inspiration gallery as APPROVED.
 *
 * Reachable at /admin/clip. Auto-prefills the URL from `?url=` so the
 * companion bookmarklet can deep-link straight in.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Link as LinkIcon, Globe, Send, Save, Bookmark, ClipboardCopy,
  ExternalLink, Check,
} from 'lucide-react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { devError } from '../utils/logger';
import { toast } from 'sonner';

const ADMIN_EMAILS = ['thisisradas@gmail.com'];

function isAdmin(user) {
  return !!user && (user.user_type === 'admin' || (user.email && ADMIN_EMAILS.includes(user.email.toLowerCase())));
}

export default function AdminClipPage() {
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [url, setUrl] = useState(searchParams.get('url') || '');
  const [scraping, setScraping] = useState(false);
  const [scraped, setScraped] = useState(null);
  const [chosenImages, setChosenImages] = useState([]);
  const [meta, setMeta] = useState({ categories: [], tags: [] });
  const [form, setForm] = useState({
    title: '', body: '', architect: '', photographer: '',
    categories: [], tags: [],
  });
  const [saving, setSaving] = useState(false);

  // Auto-scrape if the bookmarklet pre-supplied a URL
  useEffect(() => {
    const initial = searchParams.get('url');
    if (initial) {
      void scrape(initial);
    }
    // Pull the categories/tags so we can show pickers with the same vocabulary.
    axios.get(`${API}/inspirations/_meta`).then((r) => setMeta(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading) {
    return <div className="min-h-screen pt-24 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>;
  }
  if (!isAdmin(user)) return <Navigate to="/" replace />;

  const scrape = async (override) => {
    const targetUrl = (override ?? url).trim();
    if (!targetUrl) return;
    setScraping(true);
    try {
      const { data } = await axios.post(`${API}/inspirations/admin/clip-url`, { url: targetUrl });
      setScraped(data);
      setUrl(data.url);
      setChosenImages(data.image_candidates.slice(0, 4));
      setForm((f) => ({
        ...f,
        title: data.title || f.title,
        body: data.description || f.body,
        architect: data.architect_guess || f.architect,
      }));
      toast.success(`Found ${data.image_candidates.length} image candidates`);
    } catch (e) {
      devError('clip-url', e);
      toast.error(e.response?.data?.detail || 'Could not scrape');
    } finally { setScraping(false); }
  };

  const toggleImage = (img) => {
    setChosenImages((prev) => prev.includes(img) ? prev.filter((p) => p !== img) : [...prev, img]);
  };

  const toggleSet = (field, value) => {
    setForm((f) => {
      const set = new Set(f[field]);
      set.has(value) ? set.delete(value) : set.add(value);
      return { ...f, [field]: [...set] };
    });
  };

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) { toast.error('Title required'); return; }
    if (chosenImages.length === 0) { toast.error('Pick at least one image'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/inspirations/admin/clip-save`, {
        ...form,
        source_url: url,
        image_urls: chosenImages,
      });
      toast.success('Saved to the inspiration gallery');
      navigate(`/inspiration?just=${data.slug}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="admin-clip-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="mb-6">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Web Clipper</p>
          <h1 className="font-playfair text-3xl md:text-4xl font-bold mb-1">
            Send any case study into the gallery
          </h1>
          <p className="text-sm text-muted-foreground">
            Paste a URL — or use the bookmarklet below — and we'll scrape the
            article for title, description and image candidates.
          </p>
        </div>

        {/* URL bar */}
        <form
          onSubmit={(e) => { e.preventDefault(); scrape(); }}
          className="flex flex-col sm:flex-row gap-2 mb-8"
        >
          <div className="relative flex-1">
            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://www.dezeen.com/..."
              type="url"
              className="pl-9"
              data-testid="clip-url-input"
              required
            />
          </div>
          <Button type="submit" disabled={scraping} data-testid="clip-scrape-btn">
            {scraping ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Globe className="w-4 h-4 mr-2" />}
            Scrape
          </Button>
        </form>

        <BookmarkletCard />

        {scraped && (
          <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8">
            {/* Image picker */}
            <div>
              <h2 className="font-playfair text-xl mb-3">Pick images ({chosenImages.length} selected)</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="clip-image-grid">
                {scraped.image_candidates.map((img) => {
                  const chosen = chosenImages.includes(img);
                  return (
                    <button
                      key={img}
                      type="button"
                      onClick={() => toggleImage(img)}
                      className={`relative rounded-lg overflow-hidden border-2 transition-all ${
                        chosen ? 'border-accent ring-2 ring-accent/40' : 'border-transparent hover:border-border/40'
                      }`}
                      data-testid={`clip-img-${img.slice(-30)}`}
                    >
                      <img src={img} alt="" className="w-full h-32 object-cover" loading="lazy" />
                      {chosen && (
                        <span className="absolute top-2 right-2 bg-accent text-accent-foreground rounded-full p-1">
                          <Check className="w-3 h-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
              {scraped.image_candidates.length === 0 && (
                <p className="text-sm text-muted-foreground py-8">No images found on the page — you can still save with title + description.</p>
              )}
            </div>

            {/* Form */}
            <aside className="space-y-4">
              <Field label="Title *">
                <Input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  maxLength={200} data-testid="clip-title" />
              </Field>
              <Field label="Reuse story / description">
                <Textarea rows={5} value={form.body} onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
                  maxLength={4000} data-testid="clip-body" />
              </Field>
              <Field label="Architect / firm">
                <Input value={form.architect} onChange={(e) => setForm((f) => ({ ...f, architect: e.target.value }))}
                  data-testid="clip-architect" />
              </Field>
              <Field label="Photographer">
                <Input value={form.photographer} onChange={(e) => setForm((f) => ({ ...f, photographer: e.target.value }))} />
              </Field>
              <Field label="Source URL">
                <a href={url} target="_blank" rel="noreferrer" className="text-xs text-accent hover:underline inline-flex items-center gap-1 break-all">
                  {url} <ExternalLink className="w-3 h-3 flex-shrink-0" />
                </a>
              </Field>
              {meta.categories.length > 0 && (
                <Field label="Categories">
                  <div className="flex flex-wrap gap-1.5">
                    {meta.categories.map((c) => (
                      <button key={c} type="button" onClick={() => toggleSet('categories', c)}
                        className={`text-xs px-2.5 py-1 rounded-full transition-colors ${form.categories.includes(c) ? 'bg-accent/20 text-accent border border-accent/40' : 'border border-border/30 text-muted-foreground'}`}>
                        {c}
                      </button>
                    ))}
                  </div>
                </Field>
              )}
              {meta.tags.length > 0 && (
                <Field label="Tags">
                  <div className="flex flex-wrap gap-1.5">
                    {meta.tags.map((t) => (
                      <button key={t} type="button" onClick={() => toggleSet('tags', t)}
                        className={`text-[11px] px-2 py-0.5 rounded-full transition-colors ${form.tags.includes(t) ? 'bg-accent/20 text-accent border border-accent/40' : 'border border-border/30 text-muted-foreground'}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                </Field>
              )}
              <Button type="submit" disabled={saving} className="w-full" data-testid="clip-save-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save to inspiration gallery
              </Button>
            </aside>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{label}</label>
      {children}
    </div>
  );
}

function BookmarkletCard() {
  // The bookmarklet just opens our admin page with the current page's URL
  // attached as a query param. We do all the heavy scraping server-side.
  const code = useMemo(() => {
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://patinamart.com';
    return (
      `javascript:(function(){` +
      `var u=encodeURIComponent(location.href);` +
      `window.open('${origin}/admin/clip?url='+u,'_blank');` +
      `})();`
    );
  }, []);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success('Bookmarklet copied');
    } catch {
      toast.error('Copy failed — select & copy the link manually');
    }
  };
  return (
    <details className="mb-8 rounded-xl border border-accent/20 bg-accent/5 p-5" data-testid="clip-bookmarklet-card">
      <summary className="cursor-pointer text-sm font-medium inline-flex items-center gap-2">
        <Bookmark className="w-4 h-4 text-accent" />
        Install the "Send to PatinaMart" bookmarklet (one-time setup)
      </summary>
      <ol className="mt-4 text-sm text-muted-foreground space-y-2 list-decimal pl-5">
        <li>Show your browser's bookmarks bar (Ctrl/Cmd-Shift-B).</li>
        <li>
          Drag this link onto your bookmarks bar:{' '}
          <a
            href={code}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-accent text-accent-foreground font-medium text-xs hover:bg-accent/90"
            onClick={(e) => e.preventDefault()}
            draggable
            data-testid="clip-bookmarklet-link"
          >
            <Bookmark className="w-3 h-3" /> Send to PatinaMart
          </a>
        </li>
        <li>
          When you're reading an article worth saving, click the bookmarklet —
          a tab opens here pre-loaded with the page's URL ready to scrape.
        </li>
        <li>
          Or copy the snippet manually and create a new bookmark with this URL:
          <button
            type="button"
            onClick={copy}
            className="block mt-2 w-full text-left text-[11px] font-mono p-2 rounded bg-card/60 border border-border/30 hover:border-accent/40 break-all"
            data-testid="clip-bookmarklet-copy"
          >
            {copied ? <span className="text-emerald-400 inline-flex items-center gap-1"><Check className="w-3 h-3" />Copied</span> : <span className="inline-flex items-center gap-1 text-accent"><ClipboardCopy className="w-3 h-3" />Click to copy</span>}
            <span className="block mt-1 text-muted-foreground">{code}</span>
          </button>
        </li>
      </ol>
    </details>
  );
}
